import {
  StorageReadError,
  StorageWriteError,
  type BestRecord,
  type Difficulty,
  type RegionScope,
  type ScoreResult,
} from "./types.ts";

const STORAGE_KEY = "earth-puzzle:v1:best-records";
const SCHEMA_VERSION = 1;

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

interface RecordFile {
  schemaVersion: number;
  records: Record<string, BestRecord>;
}

export const bestRecordKey = (regionScope: RegionScope, difficulty: Difficulty): string =>
  `${regionScope}_${difficulty}`;

export class StorageService {
  private readonly storage: StorageLike;

  constructor(storage: StorageLike | undefined = globalThis.localStorage) {
    if (!storage) throw new StorageWriteError("此環境沒有可用的 storage");
    this.storage = storage;
  }

  private readAll(): RecordFile {
    let raw: string | null;
    try {
      raw = this.storage.getItem(STORAGE_KEY);
    } catch (err) {
      throw new StorageReadError("無法讀取本地紀錄", { cause: err });
    }
    if (raw === null) return { schemaVersion: SCHEMA_VERSION, records: {} };

    let parsed: RecordFile;
    try {
      parsed = JSON.parse(raw);
    } catch (err) {
      throw new StorageReadError("本地紀錄格式損毀", { cause: err });
    }
    // 未來新增版本時在此依序套用 migration；目前只有 v1，版本不符即視為損毀
    if (parsed?.schemaVersion !== SCHEMA_VERSION || typeof parsed.records !== "object") {
      throw new StorageReadError(`無法辨識的紀錄版本：${parsed?.schemaVersion}`);
    }
    return parsed;
  }

  // 整包覆寫而非逐欄位更新，避免多分頁同時寫入時產生半寫入的損毀資料
  private writeAll(file: RecordFile): void {
    try {
      this.storage.setItem(STORAGE_KEY, JSON.stringify(file));
    } catch (err) {
      throw new StorageWriteError("本地紀錄儲存失敗", { cause: err });
    }
  }

  loadBestRecord(key: string): BestRecord | null {
    return this.readAll().records[key] ?? null;
  }

  loadAll(): Record<string, BestRecord> {
    return this.readAll().records;
  }

  saveBestRecord(record: BestRecord): void {
    const file = this.readAll();
    file.records[record.key] = record;
    this.writeAll(file);
  }

  reset(): void {
    this.writeAll({ schemaVersion: SCHEMA_VERSION, records: {} });
  }

  /** 分數高者為佳；同分時取用時較短者 */
  saveIfBetter(
    key: string,
    score: ScoreResult,
    achievedAt = new Date().toISOString(),
  ): { isNewRecord: boolean; previous: BestRecord | null } {
    const previous = this.loadBestRecord(key);
    const better =
      !previous ||
      score.finalScore > previous.bestScore ||
      (score.finalScore === previous.bestScore && score.elapsedSeconds < previous.bestTimeSeconds);

    if (better) {
      this.saveBestRecord({
        key,
        bestScore: score.finalScore,
        bestTimeSeconds: score.elapsedSeconds,
        stars: score.stars,
        achievedAt,
      });
    }
    return { isNewRecord: better, previous };
  }
}
